import { BrandColors, Colors } from "@/constants/theme";
import { formatBytesToMB, type ImagePickerAsset } from "@/lib/image-picker";
import { MAX_UPLOAD_SIZE } from "@/lib/validation/file";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { ThemedText } from "../themed-text";
import { Label } from "./label";

export function DocumentPicker({
  label,
  asset,
  onPick,
  required,
  desktop = false,
  allowPdf = false,
}: {
  label: string;
  asset: ImagePickerAsset | null;
  onPick: () => void;
  required?: boolean;
  desktop?: boolean;
  allowPdf?: boolean;
}) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const isPdf = asset?.mimeType === "application/pdf";
  return (
    <View
      style={[pickerStyles.container, desktop && pickerStyles.desktopContainer]}
    >
      <Label label={label} required={required} style={pickerStyles.label} />
      <TouchableOpacity
        style={[
          pickerStyles.picker,
          desktop && pickerStyles.desktopPicker,
          { borderColor: theme.border, backgroundColor: theme.background },
        ]}
        onPress={onPick}
      >
        {asset ? (
          <View style={pickerStyles.previewContainer}>
            {isPdf ? (
              <View style={pickerStyles.placeholder}>
                <Ionicons
                  name="document-text-outline"
                  size={32}
                  color={theme.text}
                />
                <ThemedText
                  style={pickerStyles.fileName}
                  type="extraSmall"
                  numberOfLines={1}
                >
                  {asset.fileName}
                </ThemedText>
              </View>
            ) : (
              <Image
                source={asset.uri}
                style={pickerStyles.preview}
                contentFit="cover"
              />
            )}
            <View style={pickerStyles.changeOverlay}>
              <Ionicons name="camera" size={24} color="#fff" />
              <ThemedText
                style={pickerStyles.changeText}
                type="extraSmallSemiBold"
              >
                Change
              </ThemedText>
            </View>
          </View>
        ) : (
          <View style={pickerStyles.placeholder}>
            <Ionicons
              name={desktop ? "share-outline" : "cloud-upload-outline"}
              size={desktop ? 20 : 32}
              color={theme.text}
            />
            <ThemedText
              style={
                desktop
                  ? pickerStyles.desktopPlaceholderText
                  : pickerStyles.placeholderText
              }
              type={desktop ? "extraSmallSemiBold" : "small"}
            >
              {allowPdf
                ? "Tap to take a photo or upload"
                : `Tap to upload ${label.toLowerCase()}`}
            </ThemedText>
            {desktop && (
              <ThemedText style={pickerStyles.desktopHint} type="extraSmall">
                {allowPdf ? "Image or PDF" : "Image file"} · Max{" "}
                {formatBytesToMB(MAX_UPLOAD_SIZE, 0)}
              </ThemedText>
            )}
          </View>
        )}
      </TouchableOpacity>
    </View>
  );
}

const pickerStyles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  label: {
    marginBottom: 8,
  },
  picker: {
    height: 120,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "dashed",
    overflow: "hidden",
  },
  placeholder: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  placeholderText: {
    fontSize: 14,
    color: "#666",
  },
  desktopContainer: {
    marginBottom: 0,
  },
  desktopLabel: {
    color: BrandColors.mediumGray,
    marginBottom: 6,
  },
  desktopPicker: {
    height: 104,
    borderStyle: "solid",
    borderRadius: 8,
  },
  desktopPlaceholderText: {
    textAlign: "center",
  },
  desktopHint: {
    color: BrandColors.mediumGray,
    fontSize: 10,
  },
  previewContainer: {
    flex: 1,
    position: "relative",
  },
  preview: {
    width: "100%",
    height: "100%",
  },
  fileName: {
    paddingHorizontal: 12,
  },
  changeOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.3)",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  changeText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "600",
  },
});
